/**
 * /projects — list of all projects that have meetings. Each tile opens the
 * project detail page at /projects/:id.
 *
 * Mockup v3 visual language: slim hero (asymmetric corners + parallax glow
 * + paper-grain) → numbered section divider with active count → reveal-fold
 * section block with the bone-surfaced ProjectTile grid (same component
 * the dashboard's "All projects" section uses, kept in sync via the
 * shared export from DashboardRoute).
 */
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/apiClient.ts';
import { useUiMotion } from '../lib/useUiMotion.ts';
import {
  NewProjectDialog,
} from '../components/NewProjectDialog.tsx';
import { useState } from 'react';
import {
  ProjectTile,
  type ProjectSummary,
} from './DashboardRoute.tsx';

export function ProjectsListRoute() {
  // Wires up reveal-fold + has-light + data-parallax + data-tilt + cursor
  // gutter for any opted-in elements on this page. Hook is a no-op when
  // none of those classes are present, so adding it here is safe.
  useUiMotion();

  const projectsWithCounts = useQuery({
    queryKey: ['projects-with-counts'],
    queryFn: () =>
      api.get<{ projects: ProjectSummary[] }>('/projects', {
        withCounts: true,
        limit: 200,
      }),
  });

  const [newProjectOpen, setNewProjectOpen] = useState(false);

  // Sort alphabetically by name (case-insensitive) for the full-list
  // view. The server still returns by recency — that's what the
  // Dashboard wants — but when staff are hunting for a specific
  // project, alpha order beats "which did I touch last Tuesday".
  // localeCompare handles accented characters + digits naturally.
  const active = (projectsWithCounts.data?.projects ?? [])
    .filter((p) => p.meetingCount > 0)
    .slice()
    .sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
    );

  return (
    <div className="mx-auto max-w-6xl px-8 pb-16 pt-8">
      {/* Slim hero — same family as the dashboard hero (asymmetric
          corners + pine border + halo + paper-grain + cursor-
          tracking sage glow with breathing keyframe) but compressed
          padding. data-parallax wires the cursor follow; hero-glow
          adds the radial gradient ::after. */}
      <div
        data-parallax
        className="hero-glow paper-grain relative mb-10 overflow-hidden rounded-bl-[40px] rounded-br-xl rounded-tl-xl rounded-tr-[60px] border border-pine bg-cloud px-10 py-9 motion-safe:animate-fade-in-up"
        style={{
          boxShadow:
            '0 0 32px rgba(55, 85, 66, 0.25), 0 5px 16px rgba(43, 42, 47, 0.09)',
        }}
      >
        <div className="flex flex-wrap items-end justify-between gap-8">
          <div className="min-w-0 max-w-2xl">
            <div className="text-[11px] font-semibold uppercase tracking-[0.25em] text-pine">
              All projects
            </div>
            {/* H1 sized to mockup hero--slim: 44px Aeonik 300 with
                tight tracking. Lighter weight than the dashboard
                H1 since this page is informational, not personal. */}
            <h1 className="mt-3 text-[44px] font-light leading-[1.05] tracking-tight text-charcoal">
              Projects
            </h1>
            <p className="mt-3 max-w-prose text-[15px] font-light leading-relaxed text-charcoal/80">
              Projects with at least one meeting - drafts and finalized,
              sorted alphabetically. Click any project to view its meetings.
            </p>
          </div>
          <Link
            to="/meetings/new"
            className="animate-pulse-cta inline-flex items-center gap-3 rounded-full bg-pine px-6 py-3 text-[15px] font-medium text-cloud shadow-md transition-all duration-200 hover:-translate-y-1 hover:scale-[1.02] hover:bg-pine/90 hover:shadow-xl"
          >
            <span aria-hidden className="text-lg leading-none">＋</span>
            New meeting
          </Link>
        </div>
      </div>

      {/* Section divider 01 — label is the active project count, so
          the page reads as "you have N to choose from" before the
          grid loads. Above-the-fold, so motion-safe page-load fade
          (no .reveal). */}
      <div
        className="mb-5 mt-2 flex items-center gap-4 motion-safe:animate-fade-in-up"
        style={{ animationDelay: '200ms' }}
      >
        <span className="text-[11px] font-semibold tabular-nums tracking-[0.25em] text-charcoal/50">
          01
        </span>
        <span className="text-[11px] font-semibold uppercase tracking-[0.3em] text-charcoal/70">
          {projectsWithCounts.isLoading ? '-' : `${active.length} active`}
        </span>
        <span className="h-px flex-1 bg-charcoal/15" aria-hidden />
      </div>

      {/* Section block — cloud paper-grain wrapper, h2 + "+ New
          project" pine pill in the header. Wrapped in .reveal-fold
          so the section 3D-folds open as you scroll past it (motion
          utility from globals.css; JS handler in useUiMotion). */}
      <section className="reveal-fold">
        <div className="paper-grain rounded-2xl border border-charcoal/10 bg-cloud px-7 pb-6 pt-7 shadow-sm">
          <div className="mb-5 flex items-baseline justify-between gap-3">
            <h2 className="text-[22px] font-medium tracking-tight text-charcoal">
              All projects
            </h2>
            <button
              type="button"
              onClick={() => setNewProjectOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-full bg-pine px-3.5 py-1.5 text-[12px] font-medium text-cloud transition-all hover:gap-2 hover:bg-pine-deep"
            >
              <span aria-hidden>+</span> New project
            </button>
          </div>

          {projectsWithCounts.isLoading ? (
            <div className="rounded-xl bg-white/60 px-5 py-10 text-center text-sm italic text-charcoal/60">
              Loading projects…
            </div>
          ) : projectsWithCounts.error ? (
            <div className="rounded-xl border border-terra-cotta/40 bg-terra-cotta/10 p-6 text-sm text-terra-cotta">
              Failed to load projects:{' '}
              {projectsWithCounts.error instanceof Error
                ? projectsWithCounts.error.message
                : 'unknown'}
            </div>
          ) : active.length === 0 ? (
            <div className="rounded-xl bg-white/60 px-5 py-10 text-center text-sm text-charcoal/60">
              No projects with meetings yet. Create a meeting to get started.
            </div>
          ) : (
            // Same grid + tile chrome as the dashboard's "All
            // projects" section. No max-height / overflow scroll
            // (this IS the projects index — page scrolls instead).
            <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {active.map((p) => (
                <ProjectTile key={p.id} project={p} />
              ))}
            </ul>
          )}
        </div>
      </section>

      <NewProjectDialog
        isOpen={newProjectOpen}
        onClose={() => setNewProjectOpen(false)}
      />
    </div>
  );
}
